import { usePlayer } from '../context/PlayerContext';
import { ListMusic, X, ArrowUp, ArrowDown, Play, Trash2 } from 'lucide-react';
import { useState } from 'react';

export default function QueuePanel() {
  const { queue, removeFromQueue, clearQueue, moveInQueue, currentTrack } = usePlayer();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title={`Queue (${queue.length})`}
        className="relative flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-white/10 rounded text-white/80 hover:text-white transition-colors"
        data-testid="queue-toggle"
      >
        <ListMusic className="w-4 h-4" />
        {queue.length > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[9px] font-bold flex items-center justify-center rounded-full"
            style={{ background: 'var(--accent,#DAA520)', color: '#000' }}
          >
            {queue.length}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="fixed right-3 z-50 w-[320px] max-w-[calc(100vw-1.5rem)] max-h-[60vh] flex flex-col"
            style={{
              bottom: 'calc(var(--player-height, 84px) + 8px)',
              background: 'rgba(10,10,10,0.95)',
              backdropFilter: 'blur(24px) saturate(1.6)',
              WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
            }}
            data-testid="queue-panel"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--accent,#DAA520)' }}>Up Next</p>
                <p className="text-[9px] text-white/40 mt-0.5">{queue.length === 0 ? 'Empty queue' : `${queue.length} track${queue.length === 1 ? '' : 's'}`}</p>
              </div>
              <div className="flex items-center gap-1">
                {queue.length > 0 && (
                  <button onClick={clearQueue} title="Clear all"
                    className="p-1.5 text-white/50 hover:text-red-400 transition-colors"
                    data-testid="queue-clear">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="p-1.5 text-white/50 hover:text-white transition-colors" data-testid="queue-close">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {currentTrack && (
                <div className="px-4 py-2 border-b border-white/5">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 mb-1.5">Now Playing</p>
                  <div className="flex items-center gap-2">
                    <Play className="w-3 h-3" style={{ color: 'var(--accent,#DAA520)' }} />
                    <span className="text-xs text-white truncate">{currentTrack.title}</span>
                  </div>
                </div>
              )}

              {queue.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                  <ListMusic className="w-7 h-7 text-white/20 mb-2" />
                  <p className="text-xs text-white/50 font-bold uppercase tracking-wider">Queue is empty</p>
                  <p className="text-[10px] text-white/30 mt-2 leading-relaxed">
                    Tap the queue icon next to any track, beat, or free media item to line it up.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {queue.map((t, i) => (
                    <li key={t._queueId} className="group flex items-center gap-2 px-3 py-2 hover:bg-white/5"
                      data-testid={`queue-item-${t._queueId}`}>
                      <span className="text-[10px] font-mono text-white/40 w-5 text-right flex-shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white truncate">{t.title}</p>
                        {t.artist && <p className="text-[10px] text-white/40 truncate">{t.artist}</p>}
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => moveInQueue(t._queueId, 'up')} disabled={i === 0}
                          className="p-1 text-white/50 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                          title="Move up" data-testid={`queue-up-${t._queueId}`}>
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button onClick={() => moveInQueue(t._queueId, 'down')} disabled={i === queue.length - 1}
                          className="p-1 text-white/50 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                          title="Move down" data-testid={`queue-down-${t._queueId}`}>
                          <ArrowDown className="w-3 h-3" />
                        </button>
                        <button onClick={() => removeFromQueue(t._queueId)}
                          className="p-1 text-white/50 hover:text-red-400"
                          title="Remove" data-testid={`queue-remove-${t._queueId}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
